/* eslint-disable no-magic-numbers */
/**
 * Generates inverted-pyramid 3D markers on the trajectory for each alignment
 * markup. The markers visualize the chainage-to-position mapping from the
 * trajectory metadata, serving as a validation tool for chainage-based
 * navigation.
 */
import {
  GeometryType,
  ModelGeometry,
} from '@technology/web3d-plugin-trimbim/dist/esnext/TrimBim_generated';
import { AlignmentTrimbimGenerator } from './alignment-trimbim-generator';
import {
  MarkupChainageData,
  SectionChainageData,
  TrimbimAlignmentProperties,
} from './trimbim-alignment.types';

// ---------------------------------------------------------------------------
// Shared geometry types
// ---------------------------------------------------------------------------

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface LineSeg {
  start: Vec3;
  end: Vec3;
}

export interface SegmentWithTransform {
  seg: LineSeg;
  placement: { origin: Vec3; xAxis: Vec3; yAxis: Vec3; zAxis: Vec3 };
}

// ---------------------------------------------------------------------------
// Billboard marker info collected from the source trimbim
// ---------------------------------------------------------------------------

export interface BillboardMarkerInfo {
  entityId: number;
  placementOrigin: Vec3;
  chainageValue: number;
  surfaceMaterialId: number;
  layerId: number;
  /** Billboard label text — used to match station equation markups exactly. */
  label?: string;
}

// ---------------------------------------------------------------------------
// Pyramid geometry constants
// ---------------------------------------------------------------------------

const PYRAMID_WIDTH = 0.16;
const PYRAMID_HEIGHT = PYRAMID_WIDTH * 1.5; // 0.24

// ---------------------------------------------------------------------------
// Material selection
// ---------------------------------------------------------------------------

/**
 * Finds the surface material ID to use for pyramid markers by looking for a
 * center-line entity in the already-parsed geometry. If no center-line entity
 * is found, falls back to the material of the first GC instance.
 *
 * @param srcGeometry - The already-parsed FlatBuffers ModelGeometry.
 * @param alignmentProperties - Decoded alignment properties (may be undefined).
 * @returns The 0-based material index, or 0 if no material could be determined.
 */
export function findCenterLineMaterialId(
  srcGeometry: ModelGeometry | null,
  alignmentProperties?: TrimbimAlignmentProperties[]
): number {
  if (!srcGeometry) return 0;

  // Build a set of center-line entity IDs from the alignment properties
  const centerLineEntityIds = new Set<number>();
  if (alignmentProperties) {
    for (const prop of alignmentProperties) {
      if (prop.type?.toLowerCase() === 'center-line') {
        centerLineEntityIds.add(prop.entityId);
      }
    }
  }

  // Scan instances looking for a GC instance belonging to a center-line entity
  let fallbackMaterialId = -1;
  for (let i = 0; i < srcGeometry.instancesLength(); i++) {
    const inst = srcGeometry.instances(i);
    if (!inst) continue;
    const definition = srcGeometry.definitions(inst.definitionId());
    if (!definition || definition.type() !== GeometryType.GraphicsContainer)
      continue;

    // Record the first GC material as fallback
    if (fallbackMaterialId < 0) {
      fallbackMaterialId = inst.surfaceMaterialId();
    }

    // If this GC belongs to a center-line entity, use its material
    if (centerLineEntityIds.has(inst.entityId())) {
      return inst.surfaceMaterialId();
    }
  }

  return fallbackMaterialId >= 0 ? fallbackMaterialId : 0;
}

// ---------------------------------------------------------------------------
// Pyramid mesh template
// ---------------------------------------------------------------------------

/**
 * Creates the inverted pyramid TriangleMesh template:
 * - Apex at (0,0,0) pointing down (touches trajectory)
 * - Square base at z = PYRAMID_HEIGHT above
 * - 5 positions (not a multiple of 6)
 * - 6 triangles (4 sides + 2 base)
 */
function createPyramidMesh(): {
  positions: Vec3[];
  normals: Vec3[];
  vertices: Array<{ positionIndex: number; normalIndex: number }>;
  indices: number[];
} {
  const halfWidth = PYRAMID_WIDTH / 2;
  const height = PYRAMID_HEIGHT;

  const positions: Vec3[] = [
    { x: 0, y: 0, z: 0 }, // 0: apex (bottom)
    { x: -halfWidth, y: -halfWidth, z: height }, // 1: base corner
    { x: halfWidth, y: -halfWidth, z: height }, // 2: base corner
    { x: halfWidth, y: halfWidth, z: height }, // 3: base corner
    { x: -halfWidth, y: halfWidth, z: height }, // 4: base corner
  ];

  const sideLength = Math.sqrt(height * height + halfWidth * halfWidth);
  const normalZ = -halfWidth / sideLength;
  const normalXY = height / sideLength;
  const normals: Vec3[] = [
    { x: 0, y: -normalXY, z: normalZ }, // 0: front face (-Y)
    { x: normalXY, y: 0, z: normalZ }, // 1: right face (+X)
    { x: 0, y: normalXY, z: normalZ }, // 2: back face (+Y)
    { x: -normalXY, y: 0, z: normalZ }, // 3: left face (-X)
    { x: 0, y: 0, z: 1 }, // 4: base (up)
  ];

  const vertices: Array<{ positionIndex: number; normalIndex: number }> = [
    // Side face 0 (front): apex, v2, v1
    { positionIndex: 0, normalIndex: 0 },
    { positionIndex: 2, normalIndex: 0 },
    { positionIndex: 1, normalIndex: 0 },
    // Side face 1 (right): apex, v3, v2
    { positionIndex: 0, normalIndex: 1 },
    { positionIndex: 3, normalIndex: 1 },
    { positionIndex: 2, normalIndex: 1 },
    // Side face 2 (back): apex, v4, v3
    { positionIndex: 0, normalIndex: 2 },
    { positionIndex: 4, normalIndex: 2 },
    { positionIndex: 3, normalIndex: 2 },
    // Side face 3 (left): apex, v1, v4
    { positionIndex: 0, normalIndex: 3 },
    { positionIndex: 1, normalIndex: 3 },
    { positionIndex: 4, normalIndex: 3 },
    // Base triangle 1: v1, v2, v3
    { positionIndex: 1, normalIndex: 4 },
    { positionIndex: 2, normalIndex: 4 },
    { positionIndex: 3, normalIndex: 4 },
    // Base triangle 2: v1, v3, v4
    { positionIndex: 1, normalIndex: 4 },
    { positionIndex: 3, normalIndex: 4 },
    { positionIndex: 4, normalIndex: 4 },
  ];

  const indices = Array.from({ length: 18 }, (_, i) => i);

  return { positions, normals, vertices, indices };
}

// ---------------------------------------------------------------------------
// Closest markup lookup
// ---------------------------------------------------------------------------

export function findClosestMarkup(
  markups: MarkupChainageData[],
  targetChainage: number
): MarkupChainageData | undefined {
  if (markups.length === 0) return undefined;

  let best = markups[0];
  let bestDist = Math.abs(best.chainage - targetChainage);

  for (let i = 1; i < markups.length; i++) {
    const dist = Math.abs(markups[i].chainage - targetChainage);
    if (dist < bestDist) {
      best = markups[i];
      bestDist = dist;
    }
  }

  return best;
}

// ---------------------------------------------------------------------------
// Main marker generation
// ---------------------------------------------------------------------------

/**
 * For each entity, creates ONE TriangleMesh definition containing ALL
 * pyramid markers for that entity, with vertex positions pre-transformed
 * to entity-local coordinates. Uses local placement (0,0,0).
 *
 * @param gen - The trimbim generator to add geometry to.
 * @param billboardMarkers - Billboard instance data collected from the source.
 * @param entitySectionData - Chainage metadata per entity.
 * @param entitySegmentsWithTransform - Flat segment lists per entity.
 * @param pyramidMaterialId - The material index to apply to the pyramid instances.
 */
export function addPyramidMarkers(
  gen: AlignmentTrimbimGenerator,
  billboardMarkers: BillboardMarkerInfo[],
  entitySectionData: Map<number, SectionChainageData>,
  entitySegmentsWithTransform: Map<number, SegmentWithTransform[]>,
  pyramidMaterialId: number
): void {
  if (billboardMarkers.length === 0) {
    return;
  }

  // Identity placement (shared by all pyramid instances)
  const identityPlacementId = gen.addPlacement({ x: 0, y: 0, z: 0 });

  // Unit pyramid template
  const unitPyramid = createPyramidMesh();

  // Group markers by entity
  const markersByEntity = new Map<number, BillboardMarkerInfo[]>();
  for (const marker of billboardMarkers) {
    let list = markersByEntity.get(marker.entityId);
    if (!list) {
      list = [];
      markersByEntity.set(marker.entityId, list);
    }
    list.push(marker);
  }

  for (const [entityId, markers] of markersByEntity.entries()) {
    // Build combined mesh for all pyramids in this entity
    const allPositions: Vec3[] = [];
    const allNormals: Vec3[] = [];
    const allVertices: Array<{ positionIndex: number; normalIndex: number }> =
      [];
    const allIndices: number[] = [];

    const entitySegments = entitySegmentsWithTransform.get(entityId);
    const sectionData = entitySectionData.get(entityId);

    for (let idx = 0; idx < markers.length; idx++) {
      const marker = markers[idx];

      let apexX = marker.placementOrigin.x; // fallback
      let apexY = marker.placementOrigin.y;
      let apexZ = marker.placementOrigin.z;
      let directionX = 1,
        directionY = 0;
      let perpendicularX = 0,
        perpendicularY = 1;

      if (
        entitySegments &&
        entitySegments.length > 0 &&
        sectionData &&
        sectionData.markups.length > 0
      ) {
        const closestMarkup = marker.label
          ? (sectionData.markups.find((m) => m.label === marker.label) ??
            findClosestMarkup(sectionData.markups, marker.chainageValue))
          : findClosestMarkup(sectionData.markups, marker.chainageValue);
        if (closestMarkup) {
          const web3dChainage = closestMarkup.web3dChainage;
          const segmentIndex = Math.min(
            Math.floor(web3dChainage),
            entitySegments.length - 1
          );
          if (segmentIndex >= 0) {
            const { seg: segment, placement: segmentPlacement } =
              entitySegments[segmentIndex];
            const fraction = web3dChainage - segmentIndex;
            // Interpolate position in GC definition space
            const localX =
              segment.start.x + fraction * (segment.end.x - segment.start.x);
            const localY =
              segment.start.y + fraction * (segment.end.y - segment.start.y);
            const localZ =
              segment.start.z + fraction * (segment.end.z - segment.start.z);
            // Transform position through this segment's GC instance placement
            apexX =
              segmentPlacement.origin.x +
              localX * segmentPlacement.xAxis.x +
              localY * segmentPlacement.yAxis.x +
              localZ * segmentPlacement.zAxis.x;
            apexY =
              segmentPlacement.origin.y +
              localX * segmentPlacement.xAxis.y +
              localY * segmentPlacement.yAxis.y +
              localZ * segmentPlacement.zAxis.y;
            apexZ =
              segmentPlacement.origin.z +
              localX * segmentPlacement.xAxis.z +
              localY * segmentPlacement.yAxis.z +
              localZ * segmentPlacement.zAxis.z;

            // Compute orientation from the segment direction (more accurate
            // than using adjacent billboard positions).
            // Transform segment start and end to entity-local space.
            const startEntityX =
              segmentPlacement.origin.x +
              segment.start.x * segmentPlacement.xAxis.x +
              segment.start.y * segmentPlacement.yAxis.x +
              segment.start.z * segmentPlacement.zAxis.x;
            const startEntityY =
              segmentPlacement.origin.y +
              segment.start.x * segmentPlacement.xAxis.y +
              segment.start.y * segmentPlacement.yAxis.y +
              segment.start.z * segmentPlacement.zAxis.y;
            const endEntityX =
              segmentPlacement.origin.x +
              segment.end.x * segmentPlacement.xAxis.x +
              segment.end.y * segmentPlacement.yAxis.x +
              segment.end.z * segmentPlacement.zAxis.x;
            const endEntityY =
              segmentPlacement.origin.y +
              segment.end.x * segmentPlacement.xAxis.y +
              segment.end.y * segmentPlacement.yAxis.y +
              segment.end.z * segmentPlacement.zAxis.y;
            const segDeltaX = endEntityX - startEntityX;
            const segDeltaY = endEntityY - startEntityY;
            const segLength = Math.sqrt(
              segDeltaX * segDeltaX + segDeltaY * segDeltaY
            );
            if (segLength > 1e-10) {
              directionX = segDeltaX / segLength;
              directionY = segDeltaY / segLength;
              perpendicularX = -segDeltaY / segLength;
              perpendicularY = segDeltaX / segLength;
            }
          }
        }
      }

      // ── Transform unit pyramid positions to entity-local space ────
      const positionBase = allPositions.length;
      const normalBase = allNormals.length;
      const vertexBase = allVertices.length;

      for (const position of unitPyramid.positions) {
        // Rotate by trajectory direction (X→dir, Y→perp, Z→up) and translate
        allPositions.push({
          x: apexX + position.x * directionX + position.y * perpendicularX,
          y: apexY + position.x * directionY + position.y * perpendicularY,
          z: apexZ + position.z,
        });
      }

      for (const normal of unitPyramid.normals) {
        // Rotate normals by the same orientation
        allNormals.push({
          x: normal.x * directionX + normal.y * perpendicularX,
          y: normal.x * directionY + normal.y * perpendicularY,
          z: normal.z,
        });
      }

      for (const vertex of unitPyramid.vertices) {
        allVertices.push({
          positionIndex: positionBase + vertex.positionIndex,
          normalIndex: normalBase + vertex.normalIndex,
        });
      }

      for (const index of unitPyramid.indices) {
        allIndices.push(vertexBase + index);
      }
    }

    if (allPositions.length === 0) continue;

    // Compute bounding box from all vertex positions
    const meshBoundingBoxMin: Vec3 = { x: Infinity, y: Infinity, z: Infinity };
    const meshBoundingBoxMax: Vec3 = {
      x: -Infinity,
      y: -Infinity,
      z: -Infinity,
    };
    for (const position of allPositions) {
      meshBoundingBoxMin.x = Math.min(meshBoundingBoxMin.x, position.x);
      meshBoundingBoxMin.y = Math.min(meshBoundingBoxMin.y, position.y);
      meshBoundingBoxMin.z = Math.min(meshBoundingBoxMin.z, position.z);
      meshBoundingBoxMax.x = Math.max(meshBoundingBoxMax.x, position.x);
      meshBoundingBoxMax.y = Math.max(meshBoundingBoxMax.y, position.y);
      meshBoundingBoxMax.z = Math.max(meshBoundingBoxMax.z, position.z);
    }

    const meshDefinitionId = gen.addTriangleMesh(
      {
        positions: allPositions,
        normals: allNormals,
        vertices: allVertices,
        indices: allIndices,
      },
      meshBoundingBoxMin,
      meshBoundingBoxMax
    );

    // Single instance with identity placement
    gen.addInstance({
      localPlacementId: identityPlacementId,
      entityId,
      definitionId: meshDefinitionId,
      surfaceMaterialId: pyramidMaterialId,
      layerId: markers[0].layerId,
    });
  }
}
